import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 24;
      const yy = (e.clientY / window.innerHeight - 0.5) * 24;
      setPos({ x, y: yy });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden bg-black"
    >
      {/* background video */}
      <video
        data-testid="hero-video"
        className="absolute inset-0 w-full h-full object-cover"
        src="/hero-video.mp4"
        poster="/hero-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
      />
      {/* dark scrims for legibility */}
      <div className="absolute inset-0 bg-black/50 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 45%, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 40%, #000 100%)",
        }}
      />

      <motion.div style={{ y, opacity }} className="relative flex flex-col items-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ x: pos.x, translateY: pos.y }}
          className="mb-10"
        >
          <img
            src="/enso-logo-v2-transparent.png"
            alt="Enso Kitchen"
            data-testid="hero-logo"
            className="w-72 md:w-[32rem] h-auto select-none"
            draggable={false}
          />
        </motion.div>

        <h1 className="font-logo text-center leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light uppercase tracking-[0.15em] text-[#F5F2EB]">
          <Line delay={0.5}>Coming</Line>
          <Line delay={0.65}>
            <span className="text-[#B5643C]">Soon</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="max-w-3xl text-center text-2xl md:text-4xl text-[#F5F2EB]/90 mt-10 leading-snug font-serif-display italic"
        >
          Inspired food. Meaningful gatherings. Unforgettable experiences.
        </motion.p>
      </motion.div>
    </section>
  );
}
